import { CommandPalette } from './CommandPalette';
import { ReviewReminderBanner } from './ReviewReminder';
import { Link, NavLink, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Inbox, Settings, Sun, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageToggle } from '@/components/LanguageToggle';

const items = [
  { to: '/', labelKey: 'nav.today', icon: Sun, end: true },
  { to: '/week', labelKey: 'nav.week', icon: CalendarDays, end: false },
  { to: '/inbox', labelKey: 'nav.inbox', icon: Inbox, end: false },
  { to: '/goals', labelKey: 'nav.goals', icon: Target, end: false },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings, end: false },
];

export function AppShell() {
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[13rem_minmax(0,1fr)]">
      {/* Боковая навигация на широком экране, нижняя — на телефоне. */}
      <nav
        aria-label={t('nav.primary')}
        className={cn(
          'fixed inset-x-0 bottom-0 z-10 flex justify-around border-t border-line bg-surface',
          'pb-safe-b pl-safe-l pr-safe-r',
          'md:static md:h-dvh md:flex-col md:justify-start md:gap-1 md:border-t-0 md:border-r md:p-3 md:pt-safe-t',
        )}
      >
        <div className="hidden px-2 pb-4 pt-2 md:block">
          <span className="font-serif text-lg font-semibold">{t('app.name')}</span>
        </div>
        {items.map(({ to, labelKey, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'flex min-h-11 flex-1 flex-col items-center justify-center gap-1 px-2 py-2 text-[11px]',
                'md:min-h-11 md:flex-none md:flex-row md:justify-start md:gap-3 md:rounded-lg md:px-3 md:text-sm',
                isActive ? 'text-accent md:bg-accent-soft' : 'text-muted hover:text-ink',
              )
            }
          >
            <Icon aria-hidden="true" className="size-5 md:size-4" />
            <span>{t(labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="flex min-w-0 flex-col pt-safe-t pl-safe-l pr-safe-r">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 md:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <CommandPalette />
            <p className="hidden text-sm text-muted lg:block">{t('app.tagline')}</p>
            <Link
              className="inline-flex min-h-11 min-w-11 items-center justify-center text-sm text-accent"
              to="/review"
            >
              {t('review.shortTitle')}
            </Link>
            <Link
              className="inline-flex min-h-11 min-w-11 items-center justify-center text-sm text-accent"
              to="/statistics"
            >
              {t('statistics.title')}
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </header>
        {/* Отступ снизу — под нижнюю навигацию и домашний индикатор. */}
        <main className="flex-1 px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-6 md:px-8 md:pb-10">
          <ReviewReminderBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
